import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import {
  IconCard,
  Radio,
  Skeleton,
} from 'gatorcomponents';
import { CC_FLAGS, FAST_CHECKOUT_CREDIT_CARD_METHODS } from '@/pages/FastCheckout/FastCheckout.enum.jsx';
import * as Styles from './FastCheckoutCC.styles';
import masterCardFlag from '@/pages/FastCheckout/media/mastercard.svg';
import visaFlag from '@/pages/FastCheckout/media/visa.svg';
import withErrorBoundary from '@/utils/errorBoundary';
import { format, parse } from 'date-fns';
import { config } from '@/config';

const FastCheckoutCC = ({
  iframe,
  locale,
  renewalDate,
  creditCardMethod,
  setCreditCardMethod,
  currentCreditCard,
  isIframeLoaded,
  isMobile,
  description,
  iframeInLoading,
}) => {
  const {
    ccAlreadyRegistered, newCC, alreadyRegisteredTitle, expire, nextPayment, dateGrouper,
  } = locale;

  const hasACreditCardRegistered = currentCreditCard && currentCreditCard.id && currentCreditCard.cardLastFour;

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (hasACreditCardRegistered && isFirstRender.current) {
      setCreditCardMethod(FAST_CHECKOUT_CREDIT_CARD_METHODS.ALREADY_REGISTERED);
      isFirstRender.current = false;
    }
  }, [hasACreditCardRegistered, setCreditCardMethod]);

  const renderMaskedLastFour = useCallback((CCNumber) => {
    const CCNumberWithoutLastFour = CCNumber.slice(0, -4);
    const blocks = [];
    for (let i = 0; i < CCNumberWithoutLastFour.length; i += 4) {
      blocks.push({ value: CCNumberWithoutLastFour.slice(i, i + 4), id: i });
    }

    return (
      <>
        {
          blocks.map(block => (
            <Styles.CCNumberBlock key={block.id}>
              {block.value.split('').map((char, index) => (char === '*' ? (
                <Styles.CensuredNumber key={`${block.id}-${index.toString()}`} />
              ) : null))}
            </Styles.CCNumberBlock>
          ))
        }
        <Styles.CCLastFour>{CCNumber.slice(-4)}</Styles.CCLastFour>
      </>
    );
  }, []);

  const formatRenewalDate = useMemo(() => {
    if (renewalDate) {
      const date = parse(renewalDate, 'dd/MM/yyyy', new Date());
      return format(date, `d '${dateGrouper}' MMMM '${dateGrouper}' yyyy`, { locale: config.LANG.dateFns });
    }
  }, [renewalDate, dateGrouper]);

  const ccFlag = useMemo(() => {
    switch (currentCreditCard.cardType) {
      case CC_FLAGS.MASTERCARD:
        return <Styles.CCFlag src={masterCardFlag} alt="Mastercard" />;
      case CC_FLAGS.VISA:
        return <Styles.CCFlag src={visaFlag} alt="Visa" />;
      default:
        return <IconCard />;
    }
  }, [currentCreditCard.cardType]);

  const iframeSkeleton = (
    <Styles.IframeSkeletonWrapper>
      <Skeleton width="308px" height="16px" />
      {hasACreditCardRegistered && (
      <Styles.SkeletonRadioWrapper>
        <Skeleton width="236px" height="64px" />
        <Skeleton width="236px" height="64px" />
      </Styles.SkeletonRadioWrapper>
      )}
      <Styles.SkeletonFlagsWrapper>
        <Skeleton width="41px" height="28px" />
        <Skeleton width="41px" height="28px" />
        <Skeleton width="41px" height="28px" />
        <Skeleton width="41px" height="28px" />
      </Styles.SkeletonFlagsWrapper>
      <Styles.SkeletonFieldsWrapper>
        <Skeleton width="100%" height="44px" />
      </Styles.SkeletonFieldsWrapper>
      <Styles.SkeletonFieldsWrapper>
        <Skeleton width="100%" height="44px" />
      </Styles.SkeletonFieldsWrapper>
      <Styles.SkeletonFieldsWrapper>
        <Styles.YearCCWrapper>
          <Skeleton width="50%" height="44px" />
          <Skeleton width="50%" height="44px" />
        </Styles.YearCCWrapper>
        <Styles.SkeletonCvvWrapper>
          <Skeleton width={isMobile ? '80%' : '61%'} height="44px" />
          <Skeleton width={isMobile ? '20%' : '39%'} height="44px" />
        </Styles.SkeletonCvvWrapper>
      </Styles.SkeletonFieldsWrapper>
      {isMobile && (
        <Styles.FastCheckoutButtonSkeletonWrapper>
          <Skeleton width="100%" height="50px" />
        </Styles.FastCheckoutButtonSkeletonWrapper>
      )}
      <Styles.SkeletonFooterWrapper>
        <Skeleton width="100%" height="16px" />
        <Skeleton width="100%" height="16px" />
      </Styles.SkeletonFooterWrapper>
    </Styles.IframeSkeletonWrapper>
  );

  return (
    <>
      <Styles.HideInIframeLoadWrapper shouldHide={iframeInLoading}>
        <Styles.PaymentInformation>{description}</Styles.PaymentInformation>
        {hasACreditCardRegistered && (
        <Styles.RadiosWrapper>
          <Styles.RadioWrapper
            htmlFor="#alreadyRegisteredRadio"
            data-testid="alreadyRegistered"
            onClick={() => setCreditCardMethod(FAST_CHECKOUT_CREDIT_CARD_METHODS.ALREADY_REGISTERED)}
          >
            <Radio checked={creditCardMethod === FAST_CHECKOUT_CREDIT_CARD_METHODS.ALREADY_REGISTERED} variant="blue" id="alreadyRegisteredRadio" />
            <Styles.RadioLabel>{ccAlreadyRegistered}</Styles.RadioLabel>
          </Styles.RadioWrapper>
          <Styles.RadioWrapper
            htmlFor="#newCCRadio"
            data-testid="newCC"
            onClick={() => setCreditCardMethod(FAST_CHECKOUT_CREDIT_CARD_METHODS.NEW)}
          >
            <Radio checked={creditCardMethod === FAST_CHECKOUT_CREDIT_CARD_METHODS.NEW} variant="blue" id="newCCRadio" />
            <Styles.RadioLabel>{newCC}</Styles.RadioLabel>
          </Styles.RadioWrapper>
        </Styles.RadiosWrapper>
        )}
      </Styles.HideInIframeLoadWrapper>


      <Styles.IframeWrapper shouldHide={iframeInLoading || creditCardMethod === FAST_CHECKOUT_CREDIT_CARD_METHODS.ALREADY_REGISTERED}>
        {iframe}
      </Styles.IframeWrapper>

      {(!isIframeLoaded && creditCardMethod === FAST_CHECKOUT_CREDIT_CARD_METHODS.NEW) && (
        <>
          {iframeSkeleton}
        </>
      )}

      {creditCardMethod === FAST_CHECKOUT_CREDIT_CARD_METHODS.ALREADY_REGISTERED && (
        <Styles.AlreadyRegisteredWrapper>
          <Styles.AlreadyRegisteredTitle>{alreadyRegisteredTitle}</Styles.AlreadyRegisteredTitle>
          <Styles.CardInfoWrapper>
            <Styles.FlagNumberWrapper>
              <Styles.CCFlagWrapper>
                {ccFlag}
              </Styles.CCFlagWrapper>
              <Styles.CCNumberWrapper>
                <Styles.CCLastFourWrapper>
                  {renderMaskedLastFour(currentCreditCard.cardLastFour)}
                </Styles.CCLastFourWrapper>
              </Styles.CCNumberWrapper>
            </Styles.FlagNumberWrapper>
            <Styles.CCExpireDate>
              {`${expire} ${currentCreditCard.expDate}`}
            </Styles.CCExpireDate>
          </Styles.CardInfoWrapper>
          <Styles.NextPaymentWarning>{nextPayment}</Styles.NextPaymentWarning>
          <Styles.RenewalDate>{formatRenewalDate}</Styles.RenewalDate>
        </Styles.AlreadyRegisteredWrapper>
      )}
    </>
  );
};

export default withErrorBoundary(FastCheckoutCC, 'FastCheckoutCC');
